// Validation utilities for Indian business documents

/**
 * Validate PAN Card Number
 * Format: AAAAA9999A (5 letters, 4 digits, 1 letter)
 * Example: ABCDE1234F
 */
export const validatePAN = (pan) => {
  if (!pan) return { valid: false, message: 'PAN number is required' };

  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

  if (!panRegex.test(pan.toUpperCase())) {
    return {
      valid: false,
      message: 'Invalid PAN format. It should be 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)'
    };
  }

  return { valid: true, message: 'Valid PAN number' };
};

/**
 * Validate GST Number
 * Format: 99AAAAA9999A9Z9 (15 characters)
 * - First 2 digits: State code (01-37)
 * - Next 10 characters: PAN of the business
 * - 13th character: Entity number (1-9, A-Z)
 * - 14th character: Z (default)
 * - 15th character: Check digit
 * Example: 27ABCDE1234F1Z5
 */
export const validateGST = (gst, pan = null) => {
  if (!gst) return { valid: false, message: 'GST number is required' };

  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  if (!gstRegex.test(gst.toUpperCase())) {
    return {
      valid: false,
      message: 'Invalid GST format. It should be 15 characters (e.g., 27ABCDE1234F1Z5)'
    };
  }

  // Validate state code (01-37)
  const stateCode = parseInt(gst.substring(0, 2));
  if (stateCode < 1 || stateCode > 37) {
    return {
      valid: false,
      message: 'Invalid state code in GST number. State code should be between 01 and 37'
    };
  }

  // If PAN is provided, verify it matches the PAN embedded in GST
  if (pan) {
    const gstPAN = gst.substring(2, 12).toUpperCase();
    if (gstPAN !== pan.toUpperCase()) {
      return {
        valid: false,
        message: 'PAN number in GST does not match the provided PAN card number'
      };
    }
  }

  return { valid: true, message: 'Valid GST number' };
};

/**
 * Validate FSSAI License Number
 * Format: 14 digits
 * Example: 12345678901234
 */
export const validateFSSAI = (fssai) => {
  if (!fssai) return { valid: false, message: 'FSSAI number is required' };

  const fssaiRegex = /^[0-9]{14}$/;

  if (!fssaiRegex.test(fssai)) {
    return {
      valid: false,
      message: 'Invalid FSSAI format. It should be exactly 14 digits'
    };
  }

  return { valid: true, message: 'Valid FSSAI license number' };
};

/**
 * Validate IFSC Code
 * Format: AAAA0999999 (4 letters, 5th character is 0, 6 digits)
 * Example: SBIN0001234
 */
export const validateIFSC = (ifsc) => {
  if (!ifsc) return { valid: false, message: 'IFSC code is required' };

  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;

  if (!ifscRegex.test(ifsc.toUpperCase())) {
    return {
      valid: false,
      message: 'Invalid IFSC format. It should be 4 letters, 0, then 6 characters (e.g., SBIN0001234)'
    };
  }

  return { valid: true, message: 'Valid IFSC code' };
};

/**
 * Validate all documents at once
 */
export const validateAllDocuments = (documents) => {
  const errors = {};

  // Validate PAN
  const panValidation = validatePAN(documents.pan.number);
  if (!panValidation.valid) {
    errors.pan = panValidation.message;
  }

  // Validate GST with PAN cross-check
  const gstValidation = validateGST(documents.gst.number, documents.pan.number);
  if (!gstValidation.valid) {
    errors.gst = gstValidation.message;
  }

  // Validate FSSAI
  const fssaiValidation = validateFSSAI(documents.fssai.number);
  if (!fssaiValidation.valid) {
    errors.fssai = fssaiValidation.message;
  }

  // Validate IFSC
  const ifscValidation = validateIFSC(documents.bankAccount.ifscCode);
  if (!ifscValidation.valid) {
    errors.ifsc = ifscValidation.message;
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};
